/**
 * Cognito session and token storage (shared by API client and SSE streams).
 */

import { fetchAuthSession } from 'aws-amplify/auth';
import { authApi } from '../api/generated/auth.api.js';
import type { TokenPair } from '@cnapse/shared/schemas.js';
import { apiClient } from '../api/ApiClient.js';

class SessionService {
  private static instance: SessionService;
  private accessToken: string | null = null;
  // @ts-expect-error - Managed by Cognito/Amplify, stored for reference only
  private _refreshToken: string | null = null;

  private constructor() {
    this.loadTokensFromStorage();
  }

  public static getInstance(): SessionService {
    if (!SessionService.instance) {
      SessionService.instance = new SessionService();
    }
    return SessionService.instance;
  }

  private loadTokensFromStorage(): void {
    this.accessToken = localStorage.getItem('wafer_access_token');
    this._refreshToken = localStorage.getItem('wafer_refresh_token');
  }

  public saveTokensToStorage(tokens: TokenPair): void {
    this.accessToken = tokens.accessToken;
    this._refreshToken = tokens.refreshToken;
    localStorage.setItem('wafer_access_token', tokens.accessToken);
    localStorage.setItem('wafer_refresh_token', tokens.refreshToken);
    apiClient.saveTokensToStorage(tokens);
  }

  public clearTokensFromStorage(): void {
    this.accessToken = null;
    this._refreshToken = null;
    localStorage.removeItem('wafer_access_token');
    localStorage.removeItem('wafer_refresh_token');
    localStorage.removeItem('wafer_inventory_user');
    apiClient.clearTokensFromStorage();
  }

  private async getFreshAccessToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken?.toString();

      if (accessToken) {
        this.accessToken = accessToken;
        localStorage.setItem('wafer_access_token', accessToken);
        return accessToken;
      }

      return null;
    } catch (error) {
      console.warn('Failed to get fresh token from Amplify, using cached token:', error);
      return this.accessToken || localStorage.getItem('wafer_access_token');
    }
  }

  public getSseAccessToken(): Promise<string | null> {
    return this.getFreshAccessToken();
  }

  public async logout(): Promise<void> {
    try {
      await authApi.logout();
    } catch (error) {
      console.warn('Logout API call failed:', error);
    } finally {
      this.clearTokensFromStorage();
    }
  }

  private isTokenExpired(token: string): boolean | null {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp < currentTime;
    } catch (error) {
      console.error('Error parsing token:', error);
      return null;
    }
  }

  public isAuthenticated(): boolean {
    if (!this.accessToken) {
      return false;
    }

    const expired = this.isTokenExpired(this.accessToken);
    if (expired === true) {
      this.clearTokensFromStorage();
      return false;
    }

    return true;
  }

  public getAccessToken(): string | null {
    return this.accessToken;
  }

  public async ensureAccessToken(): Promise<void> {
    await this.getFreshAccessToken();
  }

  public clearSession(): void {
    this.clearTokensFromStorage();
  }
}

export const sessionService = SessionService.getInstance();
